import MyLocationIcon from '@mui/icons-material/MyLocation';
import { TabContext, TabPanel } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Field } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import TimeZoneAutocomplete from '@sophocles/components/TimeZoneAutocomplete';
import { selectUnitModels } from '@sophocles/features/reference';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';

// Filled multiline vertically centers a short value inside a tall slot;
// anchor the textarea to the top so typed text lays out from line 1 down.
const multilineSx = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    alignSelf: 'flex-start',
    padding: '0 !important',
  },
};

/**
 * SiteForm - Reusable site form component with tabbed interface
 *
 * This component renders form fields for creating/editing sites.
 * It's designed to be used within a Formik context.
 *
 * @param {Object} props
 * @param {Object} props.siteGroup - The site group object (contains billing requirements)
 * @param {number} props.customerId - The customer ID (for billing autocomplete)
 * @param {boolean} props.isBillboard - Whether to use billboard terminology
 * @param {boolean} props.showExtraDetails - Whether to show extra details tab (default: true)
 * @param {boolean} props.showGeneralDetails - Whether to show general details tab (default: true)
 * @param {boolean} props.showTabs - Whether to show tabs or just sections (default: true)
 * @param {boolean} props.usesCalendar - Whether to show calendar system checkbox
 * @param {Object} props.values - Formik values object
 * @param {Object} props.errors - Formik errors object
 * @param {Object} props.touched - Formik touched object
 * @param {Function} props.setFieldValue - Formik setFieldValue function
 * @param {Function} props.onTimeZoneChange - Callback when timezone changes
 */
function SiteForm({
  siteGroup,
  customerId,
  isBillboard = false,
  showExtraDetails = true,
  showGeneralDetails = true,
  showModelSelector = false,
  showTabs = true,
  usesCalendar = false,
  values,
  errors,
  touched,
  setFieldValue,
  onTimeZoneChange,
}) {
  const { t } = useTranslation();
  const siteGroupTerm = useSiteGroupTerm();
  const [currentTab, setCurrentTab] = useState('0');
  const unitModels = useSelector(selectUnitModels);

  const modelOptions = showModelSelector
    ? unitModels
        .filter(m => !m.is_obsolete || m.id === values.model)
        .map(m => ({
          id: m.id,
          label: m.display_name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
    : [];

  // Extract billing requirement from site group
  const requireBilledSiteGroup = siteGroup?.toJS?.()?.options?.require_sites_bill_alternate ?? false;
  const siteGroupId = siteGroup?.get?.('id') ?? siteGroup?.id;

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          const roundedLatitude = parseFloat(latitude).toFixed(8);
          const roundedLongitude = parseFloat(longitude).toFixed(8);
          setFieldValue('latitude', roundedLatitude);
          setFieldValue('longitude', roundedLongitude);
        },
        error => {
          console.error('Geolocation error:', error);
          alert(
            t(
              'sites.geolocationUnavailable',
              'Unable to retrieve your location. Please ensure location permissions are enabled.',
            ),
          );
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        },
      );
    } else {
      alert(t('sites.geolocationNotSupported', 'Geolocation is not supported by this browser.'));
    }
  };

  // General details section content
  const generalDetailsContent = (
    <Stack spacing={3}>
      {/* Basic Information Section */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
          {t('sites.basicInformation')}
        </Typography>
        <Stack spacing={2}>
          <Field name="name">
            {({ field, meta }) => (
              <TextField
                {...field}
                required
                fullWidth
                label={t('general.name')}
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error ? meta.error : ''}
              />
            )}
          </Field>

          <Field name="description">
            {({ field, meta }) => (
              <TextField
                {...field}
                fullWidth
                label={t('general.description')}
                size="small"
                multiline
                rows={2}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error ? meta.error : ''}
                sx={multilineSx}
              />
            )}
          </Field>

          <Field name="location_description">
            {({ field, meta }) => (
              <TextField
                {...field}
                fullWidth
                label={t('general.location')}
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error ? meta.error : ''}
              />
            )}
          </Field>
        </Stack>
      </Box>

      {showModelSelector && (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
              {t('sites.deviceModel')}
            </Typography>
            <Autocomplete
              options={modelOptions}
              getOptionLabel={option => option.label || ''}
              value={modelOptions.find(o => o.id === values.model) || null}
              onChange={(_event, option) => {
                setFieldValue('model', option ? option.id : '');
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('sites.deviceModel')}
                  helperText={t('sites.deviceModelHelper')}
                  size="small"
                  fullWidth
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </Box>
        </>
      )}

      <Divider />

      {/* GPS Location Section */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
          {t('sites.gpsLocation')}
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Field name="latitude">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  type="number"
                  label={t('general.latitude')}
                  size="small"
                  slotProps={{ htmlInput: { step: 'any' } }}
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error ? meta.error : ''}
                />
              )}
            </Field>

            <Field name="longitude">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  type="number"
                  label={t('general.longitude')}
                  size="small"
                  slotProps={{ htmlInput: { step: 'any' } }}
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error ? meta.error : ''}
                />
              )}
            </Field>

            <Button
              variant="outlined"
              onClick={getLocation}
              startIcon={<MyLocationIcon />}
              sx={{ minWidth: '120px', height: '40px', whiteSpace: 'nowrap' }}
            >
              {t('sites.auto', 'Auto')}
            </Button>
          </Stack>

          <TimeZoneAutocomplete
            value={values.time_zone}
            onChange={newValue => {
              setFieldValue('time_zone', newValue);
              if (onTimeZoneChange) {
                onTimeZoneChange(newValue);
              }
            }}
            required
            label={t('general.timeZone')}
            error={touched.time_zone && Boolean(errors.time_zone)}
            helperText={touched.time_zone && errors.time_zone}
          />
        </Stack>
      </Box>

      {/* Billing Section - Conditional */}
      {requireBilledSiteGroup && (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
              {t('general.billing')}
            </Typography>
            <SiteGroupAutocomplete
              excludeInventory
              value={values.billed_site_group}
              fullWidth
              required={requireBilledSiteGroup}
              customerId={customerId}
              ignoreIds={siteGroupId ? [siteGroupId] : []}
              label={t('general.billedEntity', {
                entity: siteGroupTerm.singular,
              })}
              filters={{
                is_virtual: false,
              }}
              error={Boolean(touched.billed_site_group && errors.billed_site_group)}
              helperText={touched.billed_site_group && errors.billed_site_group}
              onChange={(_e, siteGroupId) => {
                setFieldValue('billed_site_group', siteGroupId);
              }}
            />
          </Box>
        </>
      )}
    </Stack>
  );

  // Extra details section content
  const extraDetailsContent = (
    <Stack spacing={3}>
      {/* Security & Identification */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
          {t('sites.securityIdentification')}
        </Typography>
        <Field name="tag_number">
          {({ field, meta }) => (
            <TextField
              {...field}
              fullWidth
              label={t('sites.securityTagNumber')}
              size="small"
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error ? meta.error : ''}
            />
          )}
        </Field>
      </Box>

      <Divider />

      {/* Utility Information */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
          {t('sites.utilityInformation')}
        </Typography>
        <Stack spacing={2}>
          <Field name="utility_company">
            {({ field, meta }) => (
              <TextField
                {...field}
                fullWidth
                label={t('sites.utilityCompany')}
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error ? meta.error : ''}
              />
            )}
          </Field>

          <Field name="utility_meter_number">
            {({ field, meta }) => (
              <TextField
                {...field}
                fullWidth
                label={t('sites.utilityMeterNumber')}
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error ? meta.error : ''}
              />
            )}
          </Field>

          <Field name="utility_account_number">
            {({ field, meta }) => (
              <TextField
                {...field}
                fullWidth
                label={t('sites.utilityAccountNumber')}
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error ? meta.error : ''}
              />
            )}
          </Field>
        </Stack>
      </Box>
    </Stack>
  );

  // If showTabs is false, just render the sections directly
  if (!showTabs) {
    return (
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {showGeneralDetails && generalDetailsContent}
        {showExtraDetails && extraDetailsContent}
      </Box>
    );
  }

  // Otherwise render with tabs
  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <TabContext value={currentTab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
            {showGeneralDetails && <Tab label={t('sites.generalDetails')} value="0" />}
            {showExtraDetails && <Tab label={t('sites.extraDetails')} value="1" />}
          </Tabs>
        </Box>

        {showGeneralDetails && (
          <TabPanel value="0" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {generalDetailsContent}
          </TabPanel>
        )}

        {showExtraDetails && (
          <TabPanel value="1" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {extraDetailsContent}
          </TabPanel>
        )}
      </TabContext>
    </Box>
  );
}

export default SiteForm;
